import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './FreelancerDashboard.css';

const FreelancerProjects = () => {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectNotes, setProjectNotes] = useState([]);
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/projects/my-projects', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProjects(response.data);
      
      // Calculate stats
      const stats = {
        total: response.data.length,
        pending: response.data.filter(p => p.status === 'pending').length,
        inProgress: response.data.filter(p => p.status === 'in-progress').length,
        completed: response.data.filter(p => p.status === 'completed').length
      };
      setStats(stats);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setLoading(false);
    }
  };

  const updateProjectStatus = async (projectId, newStatus) => {
    // Add confirmation for completion
    if (newStatus === 'completed') {
      if (!window.confirm('Είστε σίγουροι ότι θέλετε να ολοκληρώσετε αυτό το έργο;')) {
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/projects/${projectId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchProjects();
      setSelectedProject(null);
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Failed to update project status');
    }
  };

  const fetchProjectNotes = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/projects/${projectId}/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setProjectNotes([]);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) {
      alert('Παρακαλώ εισάγετε σημείωση');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/projects/${selectedProject.id}/notes`, 
        { message: newNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewNote('');
      fetchProjectNotes(selectedProject.id);
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Αποτυχία προσθήκης σημείωσης');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending': { class: 'status-pending', text: 'Αναμονή' },
      'in-progress': { class: 'status-in-progress', text: 'Σε Εξέλιξη' },
      'completed': { class: 'status-completed', text: 'Ολοκληρωμένο' },
      'cancelled': { class: 'status-cancelled', text: 'Ακυρωμένο' }
    };
    const badge = badges[status] || badges['pending'];
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      'low': { class: 'priority-low', text: 'Χαμηλή' },
      'medium': { class: 'priority-medium', text: 'Μεσαία' },
      'high': { class: 'priority-high', text: 'Υψηλή' }
    };
    const badge = badges[priority] || badges['medium'];
    return <span className={`priority-badge ${badge.class}`}>{badge.text}</span>;
  };

  if (loading) {
    return <div className="loading">Φόρτωση...</div>;
  }

  return (
    <div className="freelancer-content-area">
      <div className="dashboard-header">
        <h1>Τα Έργα μου</h1>
        <p>Διαχειριστείτε τα έργα που σας έχουν ανατεθεί</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>Σύνολο Έργων</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <h3>{stats.pending}</h3>
            <p>Σε Αναμονή</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🚀</div>
          <div className="stat-content">
            <h3>{stats.inProgress}</h3>
            <p>Σε Εξέλιξη</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{stats.completed}</h3>
            <p>Ολοκληρωμένα</p>
          </div>
        </div>
      </div>

      <div className="projects-section">
        <h2>Τα Έργα μου</h2>
        {projects.length === 0 ? (
          <div className="no-projects">
            <p>Δεν έχετε ανατεθεί κανένα έργο ακόμα.</p>
          </div>
        ) : (
          <div className="projects-list">
            {projects.map(project => (
              <div key={project.id} className="project-card">
                <div className="project-header">
                  <h3>{project.title}</h3>
                  <div className="project-badges">
                    {getStatusBadge(project.status)}
                    {getPriorityBadge(project.priority)}
                  </div>
                </div>
                <p className="project-description">{project.description}</p>
                
                <div className="project-meta">
                  <div className="meta-item">
                    <strong>Κατηγορία:</strong> {project.category}
                  </div>
                  <div className="meta-item">
                    <strong>Προθεσμία:</strong> {new Date(project.deadline).toLocaleDateString('el-GR')}
                  </div>
                  <div className="meta-item">
                    <strong>Προϋπολογισμός:</strong> €{project.budget}
                  </div>
                </div>

                <div className="project-actions">
                  <button 
                    className="btn-view"
                    onClick={() => {
                      setSelectedProject(project);
                      fetchProjectNotes(project.id);
                    }}
                  >
                    Λεπτομέρειες
                  </button>
                  {project.status === 'pending' && (
                    <button 
                      className="btn-start"
                      onClick={() => updateProjectStatus(project.id, 'in-progress')}
                    >
                      Έναρξη Εργασίας
                    </button>
                  )}
                  {project.status === 'in-progress' && (
                    <button 
                      className="btn-complete"
                      onClick={() => updateProjectStatus(project.id, 'completed')}
                    >
                      Ολοκλήρωση
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedProject && (
        <div className="modal-overlay" onClick={() => setSelectedProject(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedProject.title}</h2>
              <button className="modal-close" onClick={() => setSelectedProject(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-badges">
                {getStatusBadge(selectedProject.status)}
                {getPriorityBadge(selectedProject.priority)}
              </div>
              
              <div className="modal-section">
                <h3>Περιγραφή</h3>
                <p>{selectedProject.description}</p>
              </div>

              <div className="modal-section">
                <h3>Λεπτομέρειες</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <strong>Κατηγορία:</strong>
                    <span>{selectedProject.category}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Προϋπολογισμός:</strong>
                    <span>€{selectedProject.budget}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Προθεσμία:</strong>
                    <span>{new Date(selectedProject.deadline).toLocaleDateString('el-GR')}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Δημιουργήθηκε:</strong>
                    <span>{new Date(selectedProject.createdAt).toLocaleDateString('el-GR')}</span>
                  </div>
                </div>
              </div>

              {selectedProject.requirements && (
                <div className="modal-section">
                  <h3>Απαιτήσεις</h3>
                  <p>{selectedProject.requirements}</p>
                </div>
              )}

              <div className="modal-section">
                <h3>Σημειώσεις & Σχόλια</h3>
                <div className="notes-container">
                  {projectNotes.length === 0 ? (
                    <p className="no-notes">Δεν υπάρχουν σημειώσεις για αυτό το έργο.</p>
                  ) : (
                    <div className="notes-list">
                      {projectNotes.map(note => (
                        <div key={note.id} className="note-item">
                          <div className="note-header">
                            <span className="note-author">
                              {note.userName} 
                              <span className="note-role">({note.userRole === 'freelancer' ? 'Freelancer' : note.userRole === 'admin' ? 'Admin' : 'Πελάτης'})</span>
                            </span>
                            <span className="note-date">
                              {new Date(note.createdAt).toLocaleString('el-GR')}
                            </span>
                          </div>
                          <div className="note-message">{note.message}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="add-note-form">
                    <textarea
                      className="note-input"
                      placeholder="Προσθέστε μια σημείωση..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows="3"
                    />
                    <button className="btn-add-note" onClick={addNote}>
                      Προσθήκη Σημείωσης
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                {selectedProject.status === 'pending' && (
                  <button 
                    className="btn-primary"
                    onClick={() => updateProjectStatus(selectedProject.id, 'in-progress')}
                  >
                    Έναρξη Εργασίας
                  </button>
                )}
                {selectedProject.status === 'in-progress' && (
                  <button 
                    className="btn-success"
                    onClick={() => updateProjectStatus(selectedProject.id, 'completed')}
                  >
                    Ολοκλήρωση Έργου
                  </button>
                )}
                {selectedProject.status === 'completed' && (
                  <button 
                    className="btn-warning"
                    onClick={() => updateProjectStatus(selectedProject.id, 'in-progress')}
                  >
                    Επαναφορά σε Εξέλιξη
                  </button>
                )}
                <button className="btn-secondary" onClick={() => setSelectedProject(null)}>
                  Κλείσιμο
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FreelancerBrowseProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const currentUserId = JSON.parse(localStorage.getItem('user') || '{}').id;

  useEffect(() => {
    fetchAvailableProjects();
  }, []);

  const fetchAvailableProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/projects/available', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching available projects:', error);
      setLoading(false);
    }
  };

  const hasExpressedInterest = (project) => {
    return project.interestedFreelancers && project.interestedFreelancers.includes(currentUserId);
  };

  const expressInterest = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/projects/${projectId}/interest`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Το ενδιαφέρον σας καταχωρήθηκε! Ο admin θα σας ενημερώσει σύντομα.');
      fetchAvailableProjects();
    } catch (error) {
      console.error('Error expressing interest:', error);
      if (error.response?.status === 400) {
        alert('Έχετε ήδη δηλώσει ενδιαφέρον για αυτό το έργο');
      } else {
        alert('Αποτυχία καταχώρησης ενδιαφέροντος');
      }
    }
  };

  const getStatusBadge = (status) => {
    return <span className="status-badge status-pending">Διαθέσιμο</span>;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      'low': { class: 'priority-low', text: 'Χαμηλή' },
      'medium': { class: 'priority-medium', text: 'Μεσαία' },
      'high': { class: 'priority-high', text: 'Υψηλή' }
    };
    const badge = badges[priority] || badges['medium'];
    return <span className={`priority-badge ${badge.class}`}>{badge.text}</span>;
  };

  if (loading) {
    return <div className="loading">Φόρτωση...</div>;
  }

  return (
    <div className="freelancer-content-area">
      <div className="dashboard-header">
        <h1>Διαθέσιμα Έργα</h1>
        <p>Περιηγηθείτε και δηλώστε ενδιαφέρον για νέα έργα</p>
      </div>

      <div className="projects-section">
        {projects.length === 0 ? (
          <div className="no-projects">
            <p>Δεν υπάρχουν διαθέσιμα έργα αυτή τη στιγμή.</p>
          </div>
        ) : (
          <div className="projects-list">
            {projects.map(project => {
              const isInterested = hasExpressedInterest(project);
              return (
                <div key={project.id} className={`project-card ${isInterested ? 'interested' : ''}`}>
                  <div className="project-header">
                    <h3>{project.title}</h3>
                    <div className="project-badges">
                      {getStatusBadge(project.status)}
                      {getPriorityBadge(project.priority)}
                      {isInterested && <span className="interested-badge">✓ Ενδιαφέρον Δηλωμένο</span>}
                    </div>
                  </div>
                  <p className="project-description">{project.description}</p>
                  
                  <div className="project-meta">
                    <div className="meta-item">
                      <strong>Κατηγορία:</strong> {project.category}
                    </div>
                    <div className="meta-item">
                      <strong>Προθεσμία:</strong> {new Date(project.deadline).toLocaleDateString('el-GR')}
                    </div>
                    <div className="meta-item">
                      <strong>Προϋπολογισμός:</strong> €{project.budget}
                    </div>
                  </div>

                  <div className="project-actions">
                    <button 
                      className="btn-view"
                      onClick={() => setSelectedProject(project)}
                    >
                      Λεπτομέρειες
                    </button>
                    <button 
                      className={`btn-interest ${isInterested ? 'already-interested' : ''}`}
                      onClick={() => expressInterest(project.id)}
                      disabled={isInterested}
                    >
                      {isInterested ? '✓ Ενδιαφέρον Δηλωμένο' : 'Δήλωση Ενδιαφέροντος'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedProject && (
        <div className="modal-overlay" onClick={() => setSelectedProject(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedProject.title}</h2>
              <button className="modal-close" onClick={() => setSelectedProject(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-badges">
                {getStatusBadge(selectedProject.status)}
                {getPriorityBadge(selectedProject.priority)}
              </div>
              
              <div className="modal-section">
                <h3>Περιγραφή</h3>
                <p>{selectedProject.description}</p>
              </div>

              <div className="modal-section">
                <h3>Λεπτομέρειες</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <strong>Κατηγορία:</strong>
                    <span>{selectedProject.category}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Προϋπολογισμός:</strong>
                    <span>€{selectedProject.budget}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Προθεσμία:</strong>
                    <span>{new Date(selectedProject.deadline).toLocaleDateString('el-GR')}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Δημιουργήθηκε:</strong>
                    <span>{new Date(selectedProject.createdAt).toLocaleDateString('el-GR')}</span>
                  </div>
                </div>
              </div>

              {selectedProject.requirements && (
                <div className="modal-section">
                  <h3>Απαιτήσεις</h3>
                  <p>{selectedProject.requirements}</p>
                </div>
              )}

              <div className="modal-actions">
                <button 
                  className="btn-primary"
                  onClick={() => {
                    expressInterest(selectedProject.id);
                    setSelectedProject(null);
                  }}
                >
                  Δήλωση Ενδιαφέροντος
                </button>
                <button className="btn-secondary" onClick={() => setSelectedProject(null)}>
                  Κλείσιμο
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FreelancerProfile = () => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
  }, []);

  if (!user) return <div className="loading">Φόρτωση...</div>;

  return (
    <div className="freelancer-content-area">
      <div className="dashboard-header">
        <h1>Το Προφίλ μου</h1>
        <p>Διαχειριστείτε τα στοιχεία του λογαριασμού σας</p>
      </div>

      <div className="profile-card">
        <div className="profile-section">
          <h3>Προσωπικά Στοιχεία</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Όνομα:</label>
              <span>{user.name}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{user.email}</span>
            </div>
            <div className="info-item">
              <label>Ρόλος:</label>
              <span className="role-badge">{user.role === 'freelancer' ? 'Freelancer' : user.role}</span>
            </div>
          </div>
        </div>

        {user.skills && user.skills.length > 0 && (
          <div className="profile-section">
            <h3>Δεξιότητες</h3>
            <div className="skills-list">
              {user.skills.map((skill, index) => (
                <span key={index} className="skill-tag">{skill}</span>
              ))}
            </div>
          </div>
        )}

        {user.bio && (
          <div className="profile-section">
            <h3>Βιογραφικό</h3>
            <p>{user.bio}</p>
          </div>
        )}

        {user.hourlyRate && (
          <div className="profile-section">
            <h3>Ωριαία Αμοιβή</h3>
            <p className="hourly-rate">€{user.hourlyRate}/ώρα</p>
          </div>
        )}
      </div>
    </div>
  );
};

const FreelancerEarnings = () => {
  return (
    <div className="freelancer-content-area">
      <div className="dashboard-header">
        <h1>Τα Κέρδη μου</h1>
        <p>Παρακολουθήστε τα έσοδά σας</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>€0</h3>
            <p>Συνολικά Κέρδη</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <h3>€0</h3>
            <p>Αυτόν τον Μήνα</p>
          </div>
        </div>
      </div>

      <div className="earnings-section">
        <h2>Ιστορικό Συναλλαγών</h2>
        <div className="no-data">
          <p>Δεν υπάρχουν ακόμα συναλλαγές.</p>
        </div>
      </div>
    </div>
  );
};

const FreelancerDashboard = () => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = JSON.parse(localStorage.getItem('user') || '{}');

    if (!token || userData.role !== 'freelancer') {
      navigate('/login');
      return;
    }

    setUser(userData);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (!user) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    <div className="freelancer-layout">
      <aside className="freelancer-sidebar">
        <div className="freelancer-logo">
          <img src="/staticimages/be50grlogo.png" alt="be50.gr" className="logo-image" />
          <span className="logo-text">be50.gr Freelancer</span>
        </div>

        <nav className="freelancer-nav">
          <Link to="/freelancer" className="freelancer-nav-link">
            <span className="nav-icon">📋</span>
            Τα Έργα μου
          </Link>
          <Link to="/freelancer/browse" className="freelancer-nav-link">
            <span className="nav-icon">🔍</span>
            Διαθέσιμα Έργα
          </Link>
          <Link to="/freelancer/profile" className="freelancer-nav-link">
            <span className="nav-icon">👤</span>
            Προφίλ
          </Link>
          <Link to="/freelancer/earnings" className="freelancer-nav-link">
            <span className="nav-icon">💰</span>
            Κέρδη
          </Link>
        </nav>

        <div className="freelancer-user">
          <div className="user-avatar">👤</div>
          <div className="user-info">
            <div className="user-name">{user.name}</div>
            <div className="user-role">Freelancer</div>
          </div>
          <button onClick={handleLogout} className="logout-btn" title="Αποσύνδεση">
            🚪
          </button>
        </div>
      </aside>

      <main className="freelancer-content">
        <Routes>
          <Route path="/" element={<FreelancerProjects />} />
          <Route path="/browse" element={<FreelancerBrowseProjects />} />
          <Route path="/profile" element={<FreelancerProfile />} />
          <Route path="/earnings" element={<FreelancerEarnings />} />
        </Routes>
      </main>
    </div>
  );
};

export default FreelancerDashboard;
